import { EAppTemplateType } from "templates-common-library/baseClasses/CompatibilityChecker";
import AppT9N from "../../../t9n/App/App.json";
import { isGeneralAppEnabled } from "../../utils/urlUtils";

export const templateBadgeMapping = {
  [EAppTemplateType.Manager]: "Updated",
  [EAppTemplateType.Sidebar]: "Updated",
  [EAppTemplateType.Compare]: "Updated",
  [EAppTemplateType.Atlas]: "Updated",
  [EAppTemplateType.Basic]: "Updated",
  [EAppTemplateType.Reporter]: "Updated",
  [EAppTemplateType.Notify]: "Updated",
  [EAppTemplateType.Streamflow]: "Updated",

  [EAppTemplateType.Gallery]: "Beta",
  [EAppTemplateType.WebEditor]: "Beta",
};

const tags = { ...AppT9N.tags } as const;
type tagTypes = keyof typeof tags;

const tagTypeGeneral = "general";

export const capabilitySearchMapping: [
  string | null, // title for the Category (just for reference, not used anywhere else, can be left null)
  tagTypes[], // tags to search for (come from the t9n files)
  EAppTemplateType[] // templates that search tags return
][] = [
  // General feature categories
  [
    "3D terms",
    ["3d", "pan", "rotate", "scene", "webScene"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.Basic,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
    ],
  ],
  [
    "Address",
    ["address", "label"],
    [EAppTemplateType.AttachmentViewer, EAppTemplateType.Notify],
  ],
  [
    "Chart",
    ["chart", "graph"],
    [EAppTemplateType.Charts, EAppTemplateType.Streamflow],
  ],
  [
    "Content",
    ["content"],
    [
      EAppTemplateType.Atlas,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Gallery,
      EAppTemplateType.Portfolio,
    ],
  ],
  ["Create", ["create"], [EAppTemplateType.Atlas, EAppTemplateType.Sidebar]],
  [
    "Crowdsource",
    ["crowdsource"],
    [EAppTemplateType.Manager, EAppTemplateType.Reporter],
  ],
  [
    "Embed",
    ["embed"],
    [
      EAppTemplateType.Basic,
      EAppTemplateType.Media,
      EAppTemplateType.Portfolio,
    ],
  ],
  [
    "Feature count",
    ["featureCount"],
    [
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Nearby,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Forecast",
    ["forecast"],
    [EAppTemplateType.Slider, EAppTemplateType.Streamflow],
  ],
  [
    "Group terms",
    ["categories", "category", "group"],
    [EAppTemplateType.Atlas, EAppTemplateType.CategoryGallery, EAppTemplateType.Gallery],
  ],
  [
    "Import bookmarks",
    ["importBookmarks"],
    [
      EAppTemplateType.Compare,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Insets,
    ],
  ],
  [
    "Indoors",
    ["indoors"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
    ],
  ],
  [
    "Interactive Legend component",
    ["interactive", "interactiveLegend", "zoomTo"],
    [EAppTemplateType.Atlas, EAppTemplateType.InteractiveLegend],
  ],
  [
    "Present",
    ["present"],
    [EAppTemplateType.AttachmentViewer, EAppTemplateType.Exhibit],
  ],
  [
    "Related records",
    [
      "attachment",
      "attachments",
      "collection",
      "records",
      "related",
      "relatedRecords",
      "relatedTable",
    ],
    [
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Manager,
      EAppTemplateType.Reporter,
    ],
  ],
  [
    "Report",
    ["report"],
    [
      EAppTemplateType.Nearby,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Review",
    ["dataReview", "review"],
    [EAppTemplateType.AttachmentViewer, EAppTemplateType.Manager],
  ],
  [
    "Search results",
    [
      "buffer",
      "distance",
      "distanceSlider",
      "distanceValues",
      "groupResults",
      "lookup",
      "maximum",
      "minimum",
      "proximity",
      "radius",
      "results",
      "within",
      "zones",
    ],
    [
      EAppTemplateType.Nearby,
      EAppTemplateType.Notify,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Selection",
    ["featureSelection", "selection", "select",],
    [
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Manager,
      EAppTemplateType.Notify,
      EAppTemplateType.WebEditor,
    ],
  ],
  [
    "Snapping",
    [
      "snap",
      "snapping",
      "sidePanel",],
    [
      EAppTemplateType.Sidebar,
      EAppTemplateType.WebEditor,
    ],
  ],
  [
    "Sign in",
    ["logIn", "signIn"],
    [
      EAppTemplateType.Atlas,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Gallery,
      EAppTemplateType.Reporter,
    ],
  ],
  [
    "Sketch",
    ["sketch","sketching"],
    [EAppTemplateType.Atlas, EAppTemplateType.Basic, EAppTemplateType.Sidebar],
  ],
  [
    "Sort",
    ["sort", "sortBy"],
    [
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Compare,
      EAppTemplateType.Countdown,
      EAppTemplateType.Gallery,
      EAppTemplateType.Insets,
      EAppTemplateType.Manager,
      EAppTemplateType.Streamflow,
    ],
  ],
  [
    "Statistics",
    ["graphic", "statistical", "stats", "summary"],
    [
      EAppTemplateType.Charts,
      EAppTemplateType.Countdown,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Observer,
      EAppTemplateType.Slider,
    ],
  ],
  [
    "Update",
    ["update"],
    [EAppTemplateType.AttachmentViewer, EAppTemplateType.Sidebar, EAppTemplateType.WebEditor],
  ],
  [
    "Video",
    ["video"],
    [EAppTemplateType.AttachmentViewer, EAppTemplateType.Portfolio],
  ],
  [
    "Viewers",
    ["viewer"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Charts,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Streamflow,
    ],
  ],
  // App-specific categories
  [
    "3D Viewer",
    [
      "3dViewer",
      "buildingExplorer",
      "buildingSceneLayer",
      "navigate",
      "northArrow",
      "shadow",
      "sight",
      "slice",
      "spin",
    ],
    [EAppTemplateType.ThreeDViewer],
  ],
  ["Atlas", ["atlas"], [EAppTemplateType.Atlas]],
  [
    "Attachment Viewer",
    [
      "angle",
      "attachmentViewer",
      "camera",
      "download",
      "format",
      "gps",
      "height",
      "image",
      "imageDirection",
      "imagePan",
      "imageZoom",
      "photo",
      "thumbnail",
      "tour",
    ],
    [EAppTemplateType.AttachmentViewer],
  ],
  ["Basic", ["basic", "simple"], [EAppTemplateType.Basic]],
  [
    "Category Gallery",
    [
      "card",
      "categoryGallery",
      "created",
      "gallery",
      "grid",
      "itemType",
      "members",
      "modified",
      "organization",
      "owner",
      "profile",
      "user",
      "views",
    ],
    [EAppTemplateType.CategoryGallery, EAppTemplateType.Gallery
    ],
  ],
  [
    "Chart Viewer",
    [
      "bar",
      "boxPlot",
      "charts",
      "chartViewer",
      "histogram",
      "line",
      "pie",
      "scatterPlot",
    ],
    [EAppTemplateType.Charts],
  ],
  [
    "Compare",
    [
      "compare",
      "compareMaps",
      "compareScenes",
      "descriptiveText",
      "sideBySide",
      "sync",
      "synchronizeMaps",
      "synchronizePopups",
    ],
    [EAppTemplateType.Compare],
  ],
  [
    "Countdown",
    ["countdown", "highlight", "leaderboard", "pin", "ranking"],
    [EAppTemplateType.Countdown],
  ],
  [
    "Exhibit",
    [
      "duration",
      "exhibit",
      "import",
      "note",
      "notes",
      "presentation",
      "slideList",
      "slideTransitions",
      "tableOfContents",
      "transitions",
    ],
    [EAppTemplateType.Exhibit],
  ],
  ["General", [tagTypeGeneral], [EAppTemplateType.General]],
  [
    "Imagery Viewer",
    [
      "autoRefresh",
      "degree",
      "footprints",
      "imageMeasurement",
      "imageSelector",
      "imagery",
      "imageryViewer",
      "radian",
      "render",
      "rendering",
      "selector",
      "visual",
    ],
    [EAppTemplateType.ImageryApp],
  ],
  ["Insets", ["inset", "insets", "insetMap"], [EAppTemplateType.Insets]],
  [
    "Interactive Legend",
    ["count", "countsAmounts", "helpPanel", "total", "types"],
    [EAppTemplateType.InteractiveLegend],
  ],
  ["Manager", ["crowdsourceManager", "manager"], [EAppTemplateType.Manager]],
  ["Media", ["media", "mediaMap"], [EAppTemplateType.Basic]],
  [
    "Nearby",
    [
      "intersecting",
      "intersects",
      "nearby",
      "polygons",
      "resultsList",
      "route",
      "routing",
    ],
    [EAppTemplateType.Nearby],
  ],
  ["Observer", ["observer", "scoreboard"], [EAppTemplateType.Observer]],
  [
    "Portfolio",
    [
      "accordion",
      "carousel",
      "itemCollection",
      "minimize",
      "minimized",
      "navigation",
      "portfolio",
      "preload",
      "section",
      "sections",
      "series",
      "tabbed",
    ],
    [EAppTemplateType.Portfolio],
  ],
  [
    "Public Notification",
    ["notification", "notify", "publicNotification"],
    [EAppTemplateType.Notify],
  ],
  [
    "Reporter",
    [
      "comment",
      "crowdsourceReporter",
      "dislike",
      "like",
      "polling",
      "reporter",
      "vote",
    ],
    [EAppTemplateType.Reporter],
  ],
  [
    "Sidebar",
    [
      "expand",
      "expanded",
      "minimalist",
      "orientedImagery",
      "sidebar",
    ],
    [EAppTemplateType.Sidebar],
  ],
  [
    "Slider",
    [
      "animate",
      "animation",
      "number",
      "numeric",
      "range",
      "rangeValues",
      "slider",
      "temporal",
    ],
    [EAppTemplateType.Slider],
  ],
  [
    "Streamflow Viewer",
    ["streamflow", "streamflowViewer"],
    [EAppTemplateType.Streamflow],
  ],
  [
    "Web Editor",
    [
      "add",
      "copy",
      "delete",
      "merge",
      "move",
      "paste",
      "reshape",
      "split",
      "vertices",
      "webEditor",
    ],
    [EAppTemplateType.WebEditor],
  ],
  [
    "Zone Lookup",
    ["district", "draw", "zone", "zoneLookup"],
    [EAppTemplateType.ZoneLookup],
  ],
  // Capability matrix categories
  [
    "Attribute filter",
    ["attributes", "filter", "filters"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Insets,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Notify,
      EAppTemplateType.Observer,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Autoplay",
    ["autoplay", "autoPlay1"],
    [
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Slider,
    ],
  ],
  [
    "Basemap gallery",
    ["basemapGallery"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.Insets,
      EAppTemplateType.Manager,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Sidebar,
    ],
  ],
  [
    "Basemap toggle",
    ["basemapToggle"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Compare,
      EAppTemplateType.Gallery,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Bookmarks",
    ["bookmarks"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.Basic,
      EAppTemplateType.Charts,
      EAppTemplateType.Exhibit,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.WebEditor,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Slides",
    ["slide", "slides"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.Basic,
      EAppTemplateType.Exhibit,
    ],
  ],
  [
    "Compass",
    ["compass"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Gallery,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Coordinates",
    ["coordinates"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.Basic,
      EAppTemplateType.Exhibit,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Media,
      EAppTemplateType.Sidebar,
    ],
  ],
  [
    "Cover page",
    ["coverPage", "cover"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.WebEditor,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Current location",
    ["currentLocation", "locate", "location", "locations"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Gallery,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Custom CSS",
    ["customCSS"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Charts,
      EAppTemplateType.Gallery,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Custom header",
    ["customHeader"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
      EAppTemplateType.Sidebar,
    ],
  ],
  [
    "Custom logo",
    ["customLogo"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Gallery,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.WebEditor,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Custom theme",
    ["customTheme"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Gallery,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Custom URL parameter",
    ["customURLParam"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
    ],
  ],
  [
    "Daylight widget",
    ["daylight", "daylightWidget"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.Observer,
    ],
  ],
  [
    "Details panel",
    ["details", "detailsPanel"],
    [EAppTemplateType.ImageryApp, EAppTemplateType.Sidebar],
  ],
  [
    "Directions",
    ["directions"],
    [EAppTemplateType.Nearby, EAppTemplateType.ZoneLookup],
  ],
  [
    "Disable scroll",
    ["disableScroll"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Exhibit,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Media,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
    ],
  ],
  [
    "Editing",
    ["edit", "editing"],
    [
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Manager,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.WebEditor,
    ],
  ],
  [
    "Elevation profile",
    ["elevation", "elevationProfile"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.Basic,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
    ],
  ],
  [
    "Export",
    ["pdf", "export2PDF", "screenshot", "export", "print"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.Basic,
      EAppTemplateType.Compare,
      EAppTemplateType.Exhibit,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Nearby,
      EAppTemplateType.Notify,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Export to CSV",
    ["csv", "export2csv"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Notify,
      EAppTemplateType.Sidebar,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Floor filter",
    ["floorFilter", "floorPlan"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Reporter,
    ],
  ],
  [
    "Full screen",
    ["fullscreen", "fullScreen"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.WebEditor,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Home",
    ["home"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Gallery,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.WebEditor,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Hover Pop-up",
    ["hover", "hoverPop"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Media,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
    ],
  ],
  [
    "Information panel",
    ["informationPanel", "info"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
    ],
  ],
  [
    "Introduction window",
    [
      "intro",
      "introduction",
      "introductionWindow",
      "onboarding",
      "splash",
      "splashScreen",
    ],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.WebEditor,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Keyboard shortcuts",
    ["keyboardShortcuts"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Language Switcher",
    [
      "autotranslate",
      "languageSwitcher",
      "translate",
      "translationAssistant",
      "translations",
    ],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Compare,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Layer effects",
    ["effects", "layerEffects"],
    [
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Countdown,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Nearby,
      EAppTemplateType.Slider,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Layer list",
    ["layerList", "layers", "list"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.WebEditor,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Legend",
    ["legend"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Gallery,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.WebEditor,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  ["Line of sight", ["lineSight"], [EAppTemplateType.ThreeDViewer]],
  [
    "Locator map",
    ["locatorMap", "locator", "overviewMap"],
    [EAppTemplateType.ThreeDViewer, EAppTemplateType.Basic, EAppTemplateType.Media],
  ],
  ["Magnifier", ["magnifier"], [EAppTemplateType.Basic, EAppTemplateType.Media]],
  [
    "Map area",
    ["extent", "mapArea", "mapView", "navigationBoundary"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Measurement",
    ["measurement", "measure"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.Basic,
      EAppTemplateType.Exhibit,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Notify,
      EAppTemplateType.Sidebar,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Oriented imagery",
    ["orientedImagery"],
    [EAppTemplateType.ThreeDViewer, EAppTemplateType.Sidebar],
  ],
  [
    "Pop-up panel",
    ["popUp2", "popUp2Panel"],
    [
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Nearby,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Position manager",
    ["positionManager"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Gallery,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Preset theme",
    ["presetTheme"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Gallery,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Scalebar",
    ["scale", "scalebar"],
    [
      EAppTemplateType.Atlas,
      EAppTemplateType.Basic,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Search",
    ["find", "search", "searchField"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Charts,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Gallery,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.WebEditor,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Shared theme",
    ["sharedTheme"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.Gallery,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Sharing",
    ["share", "sharing", "socialSharing"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.CategoryGallery,
      EAppTemplateType.Charts,
      EAppTemplateType.Compare,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Show table",
    ["showTable", "table"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.Basic,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Sidebar,
      EAppTemplateType.WebEditor,
    ],
  ],
  ["Slice", ["slice"], [EAppTemplateType.ThreeDViewer]],
  [
    "Summary statistics",
    ["summaryStatistics"],
    [EAppTemplateType.Observer, EAppTemplateType.ZoneLookup],
  ],
  [
    "Swipe",
    ["swipe", "swipeMode"],
    [
      EAppTemplateType.Atlas,
      EAppTemplateType.Basic,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Media,
    ],
  ],
  [
    "Time",
    ["date", "dateRange", "time", "timeFilter", "timeSlider"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Media,
      EAppTemplateType.Observer,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
    ],
  ],
  [
    "Title link",
    ["titleLink", "title"],
    [
      EAppTemplateType.Countdown,
      EAppTemplateType.Insets,
      EAppTemplateType.Nearby,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.ZoneLookup,
    ],
  ],
  [
    "Weather",
    ["weather", "weatherVis"],
    [EAppTemplateType.ThreeDViewer, EAppTemplateType.Atlas],
  ],
  [
    "Zoom tools",
    ["zoomTools"],
    [
      EAppTemplateType.ThreeDViewer,
      EAppTemplateType.Atlas,
      EAppTemplateType.AttachmentViewer,
      EAppTemplateType.Basic,
      EAppTemplateType.Charts,
      EAppTemplateType.Countdown,
      EAppTemplateType.Exhibit,
      EAppTemplateType.ImageryApp,
      EAppTemplateType.Insets,
      EAppTemplateType.InteractiveLegend,
      EAppTemplateType.Manager,
      EAppTemplateType.Media,
      EAppTemplateType.Nearby,
      EAppTemplateType.Observer,
      EAppTemplateType.Portfolio,
      EAppTemplateType.Notify,
      EAppTemplateType.Reporter,
      EAppTemplateType.Sidebar,
      EAppTemplateType.Slider,
      EAppTemplateType.Streamflow,
      EAppTemplateType.WebEditor,
      EAppTemplateType.ZoneLookup,
    ],
  ],
];

export let capabilitySearchMappingObject: Record<string, EAppTemplateType[]> =
  {};

// construct the capabilitySearchMappingObject
capabilitySearchMapping.forEach((mapping) => {
  const [title, tagTypes, templates] = mapping;
  tagTypes.forEach((tagType) => {
    if (tagType === tagTypeGeneral && !isGeneralAppEnabled()) {
      return;
    }

    if (capabilitySearchMappingObject[tagType]) {
      capabilitySearchMappingObject[tagType].push(...templates);
    } else {
      capabilitySearchMappingObject[tagType] = templates;
    }
  });
});
